import { useState, useRef, useEffect } from "react";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { supabase, dbToDeal, dealToDb, updateDeal, uploadDocument, getDocumentUrl, getDocumentUrls, insertDeal } from "./supabase";
import { geocodeAddress } from "./services/geocoding";
import AppRail from "./components/common/AppRail";
import SettingsPage from "./components/common/SettingsPage";
import { BRIEF } from "./constants/briefTheme";
import ReviewPage from "./components/review/ReviewPage";
import HomeDashboard from "./components/home/HomeDashboard";
import PipelineDashboard from "./components/pipeline/PipelineDashboard";
import DealRoom from "./components/pipeline/DealRoom";
import OutreachRecord from "./components/sourcing/OutreachRecord";
import { AddDealForm, AddDealFromOM } from "./components/pipeline/AddDealForm";
import DatabaseView from "./components/sourcing/SourcingView";
import MapView from "./components/map/MapView";
import CompsView from "./components/comps/CompsView";
import InboxView from "./components/inbox/InboxView";
import LoginScreen from "./auth/LoginScreen";
import { fetchInboxMessages } from "./services/graphInbox";
import WeeklyPresentation from "./components/home/WeeklyPresentation";
import { UserProfileProvider, useUserProfile } from "./auth/UserProfileContext";
import { useGuest } from "./auth/GuestContext";
import { trackUserLogin, logActivity, autoIncrementTasks } from "./services/activityLogger";
import { parseDealName } from "./utils/parseDealName";
import { KILLED_STAGES, SOURCING_STAGES, OUTREACH_STAGES, isOutreachDeal } from "./constants/dealStages";
import { outreachStagePatch, promotePatch } from "./services/outreachFlow";
import OutreachDashboard from "./components/outreach/OutreachDashboard";
import AddDealFromLOI from "./components/outreach/AddDealFromLOI";
import KillDealModal from "./components/pipeline/KillDealModal";
import { refreshDealTiming } from "./services/dealTiming";
import IcMemoPage, { PublicIcMemo } from "./components/icmemo/IcMemoPage";
import { parseMemoPath } from "./utils/memoLink";

// Load Google Maps JS API dynamically from env var (key not in HTML source)
(function loadGoogleMaps() {
  const key = process.env.REACT_APP_GOOGLE_MAPS_KEY || process.env.REACT_APP_GOOGLE_GEOCODE_KEY;
  if (!key || typeof document === "undefined") return;
  if (document.querySelector('script[src*="maps.googleapis.com"]')) return;
  const script = document.createElement("script");
  // places  - google.maps.places.Autocomplete + PlacesService, so the address
  //           inputs across Proxy can suggest as you type.
  // geometry - spherical.computeHeading / computeDistanceBetween, which is how
  //           PropertyMedia decides which way to point the deal room's Street
  //           View. Without it google.maps.geometry is simply undefined, the
  //           heading logic silently falls through to the Street View car's own
  //           direction, and no error is raised anywhere - the panorama just
  //           quietly faces the wrong way on every deal.
  script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places,geometry&v=weekly`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
})();


// ═══════════════════════════════════════════════════════════════════════════════
// AUTH UI HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

// Page title for the slim top bar — the rail already shows which section is
// selected, so the bar just names it for orientation.
const TAB_TITLES = {
  home:     "Home",
  pipeline: "Pipeline",
  outreach: "Outreach",
  sourcing: "Sourcing",
  inbox:    "Inbox",
  comps:    "Comps",
  review:   "Review Pipeline",
  map:      "Map",
  settings: "Settings",
};

function getInitials(name, fallbackEmail) {
  if (name) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }
  if (fallbackEmail) {
    return fallbackEmail.slice(0, 2).toUpperCase();
  }
  return '?';
}

// ═══════════════════════════════════════════════════════════════════════════════
// COSTAR COLUMN MAP — maps CoStar Excel headers to our DB fields
// ═══════════════════════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════════════════════
// 4-BUCKET CATEGORIZATION
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// LOI SENDERS
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════

// DATABASE → PIPELINE HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

async function buildOutreachContext(deal) {
  let context = '';
  const searchAddr = deal.addresses?.[0]?.split(',')[0]?.trim() || '';
  if (!searchAddr) return context;

  const { data: matchingProps } = await supabase
    .from('properties')
    .select('*')
    .ilike('address', `%${searchAddr}%`);

  if (matchingProps && matchingProps.length > 0) {
    const prop = matchingProps[0];
    context += `Sourcing Property Match Found:\n`;
    context += `- Owner: ${prop.owner_name || 'Unknown'}\n`;
    context += `- RBA: ${prop.rba?.toLocaleString() || 'N/A'} SF\n`;
    if (prop.ceiling_ht) context += `- Clear Height: ${prop.ceiling_ht}\n`;
    if (prop.loading_docks) context += `- Loading Docks: ${prop.loading_docks}\n`;
    if (prop.drive_ins) context += `- Drive-Ins: ${prop.drive_ins}\n`;
    if (prop.percent_leased != null) context += `- Percent Leased: ${prop.percent_leased}%\n`;
    if (prop.construction_material) context += `- Construction: ${prop.construction_material}\n`;
    if (prop.last_sale_date) context += `- Last Sale: ${prop.last_sale_date} at $${prop.last_sale_price?.toLocaleString() || 'N/A'}\n`;

    const { data: outreach } = await supabase
      .from('outreach_log')
      .select('*')
      .eq('property_id', prop.id)
      .order('sent_at', { ascending: false });

    if (outreach && outreach.length > 0) {
      context += `\nOutreach History (${outreach.length} total):\n`;
      for (const o of outreach) {
        context += `- ${new Date(o.sent_at).toLocaleDateString()}: LOI sent by ${o.sent_by} at $${o.loi_psf}/SF ($${o.loi_total?.toLocaleString()} total). Status: ${o.status}`;
        if (o.response) context += `. Response: ${o.response}`;
        context += `\n`;
      }
    }

    if (prop.owner_name) {
      const { data: sameOwner } = await supabase
        .from('properties')
        .select('address, city, state, rba')
        .ilike('owner_name', `%${prop.owner_name}%`)
        .neq('id', prop.id)
        .limit(10);

      if (sameOwner && sameOwner.length > 0) {
        context += `\nOther Properties by Same Owner (${prop.owner_name}):\n`;
        for (const p of sameOwner) {
          context += `- ${p.address}, ${p.city || ''} ${p.state || ''} — ${p.rba?.toLocaleString() || '?'} SF\n`;
        }
      }
    }

    const { data: propContacts } = await supabase
      .from('contacts')
      .select('*')
      .or(`property_id.eq.${prop.id}${prop.owner_name ? `,owner_name.ilike.%${prop.owner_name}%` : ''}`);

    if (propContacts && propContacts.length > 0) {
      context += `\nKnown Contacts:\n`;
      for (const c of propContacts) {
        context += `- ${c.name || 'Unknown'} (${c.contact_type || 'owner'}): ${c.email1 || 'no email'}`;
        if (c.phone) context += `, ${c.phone}`;
        context += `\n`;
      }
    }
  }

  return context;
}



// ─── Shared ───────────────────────────────────────────────────────────────────



// ─── Pipeline Dashboard ───────────────────────────────────────────────────────

export default function App() {
  const msalAuthed = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { isGuest } = useGuest();

  // A shared IC Memo link opened by someone not signed in: the deal's password,
  // not Microsoft sign-in (DM, 2026-11-02). Signed-in users fall through to the
  // app, which shows them the memo with full access. While MSAL is still
  // restoring a session, render nothing rather than flash the password prompt
  // at someone who is signed in. On the local dev server (no sign-in) add
  // ?public to a memo link to see what an outsider sees.
  const memoRoute = parseMemoPath(window.location.pathname, window.location.search);
  const devLocal = process.env.NODE_ENV === "development" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
  if (memoRoute && !msalAuthed && !isGuest &&
      (!devLocal || new URLSearchParams(window.location.search).has("public"))) {
    if (inProgress !== "none") return null;
    return <PublicIcMemo route={memoRoute} />;
  }

  if (isGuest && !msalAuthed) {
    // Guest path — no MSAL account, so skip UserProfileProvider (it depends on Graph).
    return <AuthenticatedApp />;
  }

  // Local dev server only: no sign-in. Microsoft sign-in will not complete
  // against localhost, which left the dev server unusable. `npm start` sets
  // NODE_ENV=development; every build (Vercel included) sets production, so
  // this branch does not exist in a deployed bundle. Full access, as the
  // anon key already allows - there is just no named user, so Graph-backed
  // pieces (inbox, profile) stay empty.
  if (!msalAuthed && process.env.NODE_ENV === "development" &&
      ["localhost", "127.0.0.1"].includes(window.location.hostname)) {
    return <AuthenticatedApp />;
  }

  return (
    <>
      <AuthenticatedTemplate>
        <UserProfileProvider>
          <AuthenticatedApp />
        </UserProfileProvider>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
    </>
  );
}

function AuthenticatedApp() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const { isGuest, signOut: signOutGuest } = useGuest();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Track login in user_sessions on mount (once per session). Guests don't get tracked.
  useEffect(() => {
    if (account && !isGuest) {
      trackUserLogin({
        email: account.username,
        displayName: account.name,
        jobTitle: null, // populated by UserProfileContext later
      });
    }
  }, [account?.homeAccountId, isGuest]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll inbox unread count every 60 seconds for the nav badge.
  // Guests don't have a Graph-backed mailbox, so skip entirely.
  useEffect(() => {
    if (isGuest) return;
    let cancelled = false;
    async function checkUnread() {
      try {
        const { messages } = await fetchInboxMessages(instance, { top: 50, filter: "isRead eq false" });
        if (!cancelled) setInboxUnread(messages.length);
      } catch {
        // Silently fail — permissions might not be granted yet
      }
    }
    checkUnread();
    const interval = setInterval(checkUnread, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [instance, isGuest]);

  async function handleSignOut() {
    if (isGuest) {
      signOutGuest();
      return;
    }
    try {
      await instance.logoutRedirect({
        account,
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (e) {
      console.error("Sign out failed:", e);
    }
  }

  const [tab, setTab] = useState(() => {
    // Default landing tab is now "map". Stale "home" values from before the
    // Home nav was hidden get coerced back to "map" so users don't land on
    // an unreachable tab.
    const saved = localStorage.getItem('pp-active-tab');
    if (!saved) return 'home';
    return saved;
  });
  // Guests can only reach Pipeline + Map. If we restored a tab from a previous
  // (non-guest) session that's now off-limits, snap to pipeline.
  useEffect(() => {
    if (isGuest && tab !== 'pipeline' && tab !== 'map') {
      setTab('pipeline');
    }
  }, [isGuest, tab]);
  useEffect(() => { localStorage.setItem('pp-active-tab', tab); }, [tab]);
  const [view, setView] = useState("pipeline");
  // null when closed; otherwise { mode: "export" | "live", snapshot?: object }
  const [presentMode, setPresentMode] = useState(null);
  // Bumped whenever the deck saves a weekly snapshot, so Home can refresh
  // its history dropdown without the deck reaching into it.
  const [presentationSaveTick, setPresentationSaveTick] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);
  // Cross-view focus target — set when one view requests another to focus on
  // a specific item. Currently used by the Comp Database "View on Map" action,
  // but the shape ({ type, id }) generalizes to other handoffs (e.g. focus a
  // sourcing property from elsewhere). Cleared by the receiver after use.
  const [mapFocus, setMapFocus] = useState(null); // { type: 'comp', id } | null — for ephemeral cross-view jumps (comp click flies to it)
  // Multi-deal focus: ordered list of deal ids the map is currently focused on
  // for comp comparison. Capped at 3 by MapView. View on Map appends a deal
  // to this list; chips on the map remove deals via ✕.
  const [mapFocusDeals, setMapFocusDeals] = useState([]); // string[] of deal ids
  // Phase 5.1 — when true, MapView mounts straight into Present mode.
  // Set by the deal-page 🎬 Present button; consumed by MapView on first
  // render then cleared via the callback.
  const [requestPresentMap, setRequestPresentMap] = useState(false);
  const [deals, setDeals] = useState([]);
  // The app runs on the brand's dark ramp. It was authored dark, then spent a
  // period rendered light by a stylesheet that rewrote every slate class into a
  // pale equivalent; that layer is gone, and the components' own dark branches -
  // now written in the brand's values rather than Tailwind's - are the ones that
  // render. The flag stays because dozens of components still branch on it.
  const darkMode = true;
  const [dealsLoading, setDealsLoading] = useState(true);
  const [activeDeal, setActiveDeal] = useState(null);
  // Cache geocoded map properties across tab switches so MapView doesn't re-geocode
  const mapCacheRef = useRef(null);
  // Custom markets added by the user during this session
  const [customMarkets, setCustomMarkets] = useState([]);
  // Signed URLs for deal photos, keyed by deal.id — generated once on load
  const [photoUrls, setPhotoUrls] = useState({});

  function addCustomMarket(name) {
    // Avoid duplicates (case-insensitive check)
    if (!customMarkets.some((m) => m.toLowerCase() === name.toLowerCase())) {
      setCustomMarkets((prev) => [...prev, name]);
    }
  }

  useEffect(() => {
    async function fetchDeals() {
      setDealsLoading(true);
      const { data, error } = await supabase
        .from("deals")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Failed to fetch deals:", error.message);
      } else {
        const mapped = data.map(dbToDeal);
        setDeals(mapped);
        // Sign every deal photo once, in batches, so thumbnails are ready when
        // the pipeline renders. A deal shows the picture somebody chose;
        // failing that, the Street View frame captured for its coordinate. The
        // fallback is resolved here rather than in each card so the signed
        // URLs are fetched once.
        const photoOf = (d) => d.photoPath || d.streetViewPath;
        const withPhotos = mapped.filter(photoOf);
        if (withPhotos.length > 0) {
          getDocumentUrls(withPhotos.map(photoOf)).then((byPath) => {
            setPhotoUrls(Object.fromEntries(
              withPhotos.map((d) => [d.id, byPath[photoOf(d)]]).filter(([, url]) => url)));
          });
        }
      }
      setDealsLoading(false);
    }
    fetchDeals();
  }, []);

  function openDeal(deal) {
    setActiveDeal(deal);
    setView("deal");
  }

  // Open a deal from anywhere (Map, Inbox, Sourcing, Review...) in the section
  // it belongs to: a broker LOI opens under Outreach, everything else under
  // Pipeline.
  function goToDeal(deal) {
    if (!deal) return;
    setTab(isOutreachDeal(deal) ? "outreach" : "pipeline");
    setActiveDeal(deal);
    setView("deal");
  }

  // Open a deal when all you have is its id — used by the duplicate warning on
  // the add form, which knows the matching row but not the loaded deal object.
  function openDealById(id) {
    const found = deals.find(d => d.id === id);
    if (found) goToDeal(found);
  }

  function handleDealUpdated(updatedDeal) {
    setDeals((prev) => prev.map((d) => d.id === updatedDeal.id ? updatedDeal : d));
    setActiveDeal(updatedDeal);
    // A broker LOI moved into Pipeline: follow it there.
    if (tab === "outreach" && !isOutreachDeal(updatedDeal)) setTab("pipeline");
  }

  function handleDealDeleted(dealId) {
    setDeals((prev) => prev.filter((d) => d.id !== dealId));
    setActiveDeal(null);
    setView("pipeline");
  }

  // Kill-modal state — set when a user moves a deal TO a terminal stage.
  // The stage change is deferred until the modal captures reason + narrative
  // (and, for Lost, competition detail). Cancel wipes this back to null.
  const [killPending, setKillPending] = useState(null); // { deal, targetStage }

  async function handleStageChange(dealId, newStage) {
    const oldDeal = deals.find(d => d.id === dealId);
    const oldStage = oldDeal?.stage;

    if (oldDeal && isOutreachDeal(oldDeal) && OUTREACH_STAGES.includes(newStage)) {
      return applyOutreachMove(oldDeal, outreachStagePatch(oldDeal, newStage), false);
    }

    // Moving TO a terminal stage → open the Kill modal instead of writing.
    // The modal will call updateDeal + upsertCompetition itself and then
    // fire onSaved so we can swap in the fresh row + log activity.
    if (KILLED_STAGES.includes(newStage) && oldDeal) {
      setKillPending({ deal: oldDeal, targetStage: newStage, prevStage: oldStage });
      return;
    }

    // Moving OFF a terminal stage (reviving a killed deal) clears killed_at
    // so the deal isn't rendered as still-dead in review UIs.
    const extraPatch = KILLED_STAGES.includes(oldStage) && !KILLED_STAGES.includes(newStage)
      ? { killed_at: null } : {};

    const { data, error } = await supabase
      .from("deals")
      .update({ deal_stage: newStage, ...extraPatch })
      .eq("id", dealId)
      .select()
      .single();
    if (!error) {
      const updated = dbToDeal(data);
      setDeals((prev) => prev.map((d) => d.id === dealId ? updated : d));
      const dealName = parseDealName(updated.sourceNotes) || updated.addresses?.[0] || "Unknown deal";
      await logActivity({
        userName: account?.name || "Unknown",
        userEmail: account?.username || "",
        action: "stage_changed",
        description: `${dealName} moved from ${oldStage || "?"} → ${newStage}`,
        entityType: "deal",
        entityId: dealId,
        metadata: { old_stage: oldStage, new_stage: newStage, deal_name: dealName },
      });
      await autoIncrementTasks(account?.username, "stage_changed");
      // Fire-and-forget timing refresh so time_to_loi_days / time_in_uw_days
      // stay current. The activity_log write above just happened, so the
      // event this refresh reads is guaranteed to be there.
      refreshDealTiming(updated);
    }
  }

  // An Outreach (broker LOI) stage move, or its move into Pipeline.
  async function applyOutreachMove(deal, patch, promoting) {
    const { data, error } = await supabase.from("deals").update(patch).eq("id", deal.id).select().single();
    if (error) { console.error("Outreach move failed:", error.message); return; }
    const updated = { ...dbToDeal(data), photoPath: deal.photoPath };
    setDeals((prev) => prev.map((d) => d.id === deal.id ? updated : d));
    const dealName = parseDealName(updated.sourceNotes) || updated.addresses?.[0] || "Unknown deal";
    await logActivity({
      userName: account?.name || "Unknown",
      userEmail: account?.username || "",
      action: "stage_changed",
      description: promoting
        ? `${dealName} moved from Outreach (${deal.stage}) into Pipeline → ${updated.stage}`
        : `${dealName} moved from ${deal.stage || "?"} → ${updated.stage}`,
      entityType: "deal",
      entityId: deal.id,
      metadata: { old_stage: deal.stage, new_stage: updated.stage, deal_name: dealName,
                  flow: promoting ? "outreach_to_pipeline" : "outreach" },
    });
    if (promoting) refreshDealTiming(updated);
  }

  // Called by KillDealModal after it successfully persists the deal + any
  // competition row. Behaves like the tail of handleStageChange but with the
  // fully-graded row the modal already wrote.
  async function handleKillSaved({ updated: raw }) {
    if (!killPending) return;
    const updated = dbToDeal(raw);
    const oldStage = killPending.prevStage;
    setDeals(prev => prev.map(d => d.id === updated.id ? updated : d));
    // Also refresh activeDeal if we're inside the deal room while killing.
    setActiveDeal(cur => (cur?.id === updated.id ? updated : cur));
    const dealName = parseDealName(updated.sourceNotes) || updated.addresses?.[0] || "Unknown deal";
    await logActivity({
      userName: account?.name || "Unknown",
      userEmail: account?.username || "",
      action: "stage_changed",
      description: `${dealName} moved from ${oldStage || "?"} → ${updated.stage}`,
      entityType: "deal",
      entityId: updated.id,
      metadata: { old_stage: oldStage, new_stage: updated.stage, deal_name: dealName, killed_reason: updated.killedReason },
    });
    await autoIncrementTasks(account?.username, "stage_changed");
    refreshDealTiming(updated);
    setKillPending(null);
  }

  async function saveDeal(deal, setError, pendingFiles = [], photoFile = null, { allowDuplicate = false } = {}) {
    // insertDeal wraps the insert with county + MSA auto-tagging so the new
    // row participates in MSA rollups immediately (no follow-up backfill).
    // It also refuses a deal that matches one already in the book unless
    // told otherwise — the same building used to be enterable twice.
    const { data, error, duplicates } = await insertDeal(deal, { allowDuplicate });

    if (error) {
      // Hand the matches to the form so it can show WHICH deals collided and
      // offer to open one, rather than just refusing.
      if (error.code === "duplicate_deal" && duplicates?.length) {
        setError({ code: "duplicate_deal", message: error.message, duplicates });
      } else {
        setError(error.message);
      }
      return;
    }

    // Auto-geocode the new deal
    const firstAddr = deal.addresses?.[0];
    if (firstAddr) {
      const coords = await geocodeAddress(firstAddr);
      if (coords) {
        await supabase.from("deals").update({ latitude: coords.lat, longitude: coords.lng }).eq("id", data.id);
      }
    }

    const savedDeal = dbToDeal(data);

    // Upload photo first, then update the deal row with its storage path
    if (photoFile) {
      const storagePath = `${savedDeal.id}/photo_${Date.now()}_${photoFile.name}`;
      const { error: photoError } = await supabase.storage
        .from("deal-documents")
        .upload(storagePath, photoFile);

      if (!photoError) {
        await supabase.from("deals").update({ photo_path: storagePath }).eq("id", savedDeal.id);
        savedDeal.photoPath = storagePath;
        // Generate a signed URL immediately so the new card shows the thumbnail
        const url = await getDocumentUrl(storagePath);
        if (url) setPhotoUrls((prev) => ({ ...prev, [savedDeal.id]: url }));
      } else {
        console.error("Photo upload error:", photoError.message);
      }
    }

    // Upload any files the user queued in the form — we need the real deal ID first.
    // Wrap each upload individually so one failure doesn't block the others or navigation.
    for (const file of pendingFiles) {
      try {
        await uploadDocument(savedDeal.id, file);
      } catch (e) {
        console.error("Failed to upload file:", file.name, e);
      }
    }

    setDeals((prev) => [savedDeal, ...prev]);
    setView("pipeline");
    const dealName = parseDealName(savedDeal.sourceNotes) || savedDeal.addresses?.[0] || "New deal";
    await logActivity({
      userName: account?.name || "Unknown",
      userEmail: account?.username || "",
      action: "deal_created",
      description: `${dealName} added to ${isOutreachDeal(savedDeal) ? "Outreach" : "Pipeline"} (${savedDeal.stage})`,
      entityType: "deal",
      entityId: savedDeal.id,
      metadata: { stage: savedDeal.stage, deal_name: dealName, ...(isOutreachDeal(savedDeal) ? { flow: "outreach" } : {}) },
    });
    await autoIncrementTasks(account?.username, "deal_created");
  }

  // ── Render pipeline content based on internal view state ──────────────────
  function renderPipeline() {
    if (view === "deal" && activeDeal) {
      // An outreach is not a deal. Opening one into the full deal room shows
      // thesis, red flags, comp set and IRR fields that are almost always
      // blank, which teaches everyone that blank fields are normal - an
      // expensive habit on the deals where they are not.
      if (SOURCING_STAGES.includes(activeDeal.stage) && !isOutreachDeal(activeDeal)) {
        return (
          <OutreachRecord
            deal={activeDeal}
            readOnly={isGuest}
            onBack={() => setView("pipeline")}
            onPromote={async (d) => {
              // Promotion is the moment an outreach becomes a deal. The stage
              // change is all that happens here; the folder and the V1 come
              // from the underwriting pass, not from this button.
              // updateDeal converts to columns itself; handing it dealToDb()
              // output converted twice - the stage never saved, and address,
              // photo and broker fields were blanked.
              const updated = { ...d, stage: "Screening" };
              await updateDeal(d.id, updated);
              handleDealUpdated(updated);
            }}
          />
        );
      }
      return (
        <DealRoom
          deal={activeDeal}
          allDeals={deals}
          darkMode={darkMode}
          readOnly={isGuest}
          onBack={() => setView("pipeline")}
          onDealUpdated={handleDealUpdated}
          onDealDeleted={handleDealDeleted}
          onOpenKillModal={(dealArg, targetStage) => setKillPending({ deal: dealArg, targetStage, prevStage: dealArg.stage })}
          onShowOnMap={() => {
            // Append this deal to the focus list (no-op if already there).
            // Switch to the Map tab. Multi-deal compare from day 1 per Q3=B.
            setMapFocusDeals(prev => prev.includes(activeDeal.id) ? prev : [...prev, activeDeal.id]);
            setTab('map');
          }}
        />
      );
    }

    // Guests can't reach add / addFromOM — fall through to the dashboard.
    if (view === "add" && !isGuest) {
      return (
        <AddDealForm
          onSave={saveDeal}
          onCancel={() => setView("pipeline")}
          customMarkets={customMarkets}
          onAddCustomMarket={addCustomMarket}
          onOpenExisting={openDealById}
        />
      );
    }

    if (view === "addFromOM" && !isGuest) {
      return (
        <AddDealFromOM
          onSave={saveDeal}
          onCancel={() => setView("pipeline")}
          customMarkets={customMarkets}
          onAddCustomMarket={addCustomMarket}
        />
      );
    }

    return (
      <PipelineDashboard
        deals={deals}
        loading={dealsLoading}
        readOnly={isGuest}
        onSelectDeal={openDeal}
        photoUrls={photoUrls}
        onStageChange={handleStageChange}
        onAddDeal={() => setView("add")}
        onAddDealFromOM={() => setView("addFromOM")}
      />
    );
  }

  function renderOutreach() {
    if (view === "deal" && activeDeal) return renderPipeline();   // the same deal page
    if (view === "addFromLOI") {
      return (
        <AddDealFromLOI
          onSave={saveDeal}
          onCancel={() => setView("pipeline")}
          onOpenExisting={openDealById}
        />
      );
    }
    const outreachDeals = deals.filter(isOutreachDeal);
    return (
      <OutreachDashboard
        deals={outreachDeals}
        promotedCount={deals.filter((d) => !isOutreachDeal(d) && d.promotedAt).length}
        loading={dealsLoading}
        readOnly={isGuest}
        onSelectDeal={openDeal}
        photoUrls={photoUrls}
        onStageChange={handleStageChange}
        onPromote={(d) => applyOutreachMove(d, promotePatch(), true)}
        onUploadLOI={() => setView("addFromLOI")}
      />
    );
  }

  // A shared IC Memo link (/<deal-name>-ic-memo) renders the memo alone, with
  // none of the app shell - the deal page's IC Memo button opens it in a new tab.
  const memoRoute = parseMemoPath(window.location.pathname, window.location.search);
  if (memoRoute) {
    return <IcMemoPage route={memoRoute} deals={deals} dealsLoading={dealsLoading} readOnly={isGuest} />;
  }

  return (
    <div data-print-root className={`min-h-screen flex font-montserrat ${darkMode ? 'bg-[#1c1c1c] text-[#f4f4f4]' : 'text-pp-dark'}`} style={darkMode ? {} : { backgroundColor: BRIEF.ground }}>
      {/* ── Kill modal — rendered at App root so it survives view swaps ── */}
      <KillDealModal
        open={!!killPending}
        deal={killPending?.deal}
        targetStage={killPending?.targetStage}
        userName={account?.name || account?.username || null}
        darkMode={darkMode}
        onCancel={() => setKillPending(null)}
        onSaved={handleKillSaved}
      />

      {/* ── Left navigation rail ─────────────────────────────────────────────
          Replaced the centered top-tab row: seven absolutely-centered tabs
          drifted with the left/right content and capped out around that many
          items. The rail groups sections, scales past seven, owns identity,
          and gives the content the full remaining width. */}
      <AppRail
        tab={tab}
        onSelectTab={(t) => { setTab(t); setView("pipeline"); setActiveDeal(null); }}
        isGuest={isGuest}
        counts={{
          inboxUnread,
          activeDeals: deals.filter(d => !isOutreachDeal(d) && ["Underwriting", "LOI Submitted", "In Diligence"].includes(d.stage)).length,
          openOutreach: deals.filter(d => isOutreachDeal(d) && d.stage !== "Declined").length,
          totalDeals: deals.length,
        }}
        accountName={account?.name || null}
        accountEmail={account?.username || null}
        initials={getInitials(account?.name, account?.username)}
        onSignOut={handleSignOut}
      />

      {/* ── Main column ─────────────────────────────────────────────────────
          Slim top bar carries only what's contextual to the current tab —
          section nav and identity live in the rail. */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header
          data-print-hide
          className={`h-14 shrink-0 flex items-center px-6 gap-3 sticky top-0 z-40 ${darkMode ? 'bg-[#242424] border-[#3a3a3a]' : 'border-b'}`}
          style={darkMode ? {} : { backgroundColor: '#ffffff', borderColor: BRIEF.border }}
        >
          <div className="flex flex-col gap-px">
            <span className="text-[15px] font-bold tracking-tight" style={{ color: darkMode ? '#ffffff' : BRIEF.ink }}>
              {TAB_TITLES[tab] || ''}
            </span>
            <span className="text-[11px]" style={{ color: darkMode ? '#8a8a8a' : BRIEF.muted }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </span>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {tab === "outreach" && view === "pipeline" && !isGuest && (
              <button
                onClick={() => setView("addFromLOI")}
                className="flex items-center gap-2 bg-pp-red hover:bg-pp-red-light text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors font-montserrat"
              >
                <span className="text-base leading-none">📄</span>
                Upload LOI
              </button>
            )}
            {tab === "pipeline" && view === "pipeline" && !isGuest && (
              <>
                <button
                  onClick={() => setView("addFromOM")}
                  className={`flex items-center gap-1.5 font-semibold text-sm px-4 py-2 rounded-lg transition-colors border font-montserrat ${darkMode ? 'bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#f4f4f4] border-[#4a4a4a]' : 'bg-white hover:bg-[#f4f4f4] text-pp-dark border-pp-gray'}`}
                >
                  <span className="text-base leading-none">📄</span>
                  Upload OM
                </button>
                <button
                  onClick={() => setView("add")}
                  className="flex items-center gap-2 bg-pp-red hover:bg-pp-red-light text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors font-montserrat"
                >
                  <span className="text-lg leading-none">+</span>
                  Add Deal
                </button>
              </>
            )}
          </div>
        </header>

        <main data-print-main className="flex-1 min-w-0">
        {/* ── Tab Content ──────────────────────────────────────────────────────
            Guests can only reach Pipeline + Map. The other tabs are also
            gated server-side by being absent from the nav, but we belt-and-suspender
            here so a stale localStorage `pp-active-tab` (e.g. "sourcing") doesn't
            briefly mount a Graph-backed view that would crash without an MSAL
            account. */}
        {tab === "home" && !isGuest && (
          <HomeDashboard
            onNavigate={(t) => setTab(t)}
            onPresent={(opts) => setPresentMode({ mode: opts?.mode || "live", snapshot: opts?.snapshot || null })}
            presentationSaveTick={presentationSaveTick}
          />
        )}
        {tab === "pipeline" && renderPipeline()}
        {tab === "outreach" && !isGuest && renderOutreach()}
        {tab === "sourcing" && !isGuest && (
          <DatabaseView
            deals={deals}
            onDealCreated={(newDeal) => setDeals(prev => [newDeal, ...prev])}
            onNavigateToDeal={(deal) => goToDeal(deal)}
          />
        )}
        {tab === "inbox" && !isGuest && (
          <InboxView
            onNavigateToDeal={(deal) => goToDeal(deal)}
          />
        )}
        {tab === "comps" && !isGuest && (
          <CompsView
            darkMode={darkMode}
            onNavigate={(target, options = {}) => {
              // Generalised cross-view nav. Options can carry a focus payload
              // that the destination view will pick up on mount/update.
              if (target === 'map' && options.focusCompId) {
                setMapFocus({ type: 'comp', id: options.focusCompId });
              }
              setTab(target);
            }}
          />
        )}
        {tab === "map" && <MapView darkMode={darkMode} cacheRef={mapCacheRef}
          readOnly={isGuest}
          userEmail={account?.username || null}
          focusTarget={mapFocus}
          onFocusConsumed={() => setMapFocus(null)}
          focusedDealIds={mapFocusDeals}
          onChangeFocusedDealIds={setMapFocusDeals}
          availableDeals={deals}
          requestPresent={requestPresentMap}
          onPresentRequestConsumed={() => setRequestPresentMap(false)}
          onNavigateToDeal={(dealId) => {
            const deal = deals.find(d => d.id === dealId);
            if (deal) goToDeal(deal);
          }} />}
        {tab === "settings" && !isGuest && (
          <SettingsPage onBack={() => setTab('home')} />
        )}
        {tab === "review" && !isGuest && (
          <ReviewPage
            onNavigateToDeal={(dealId) => {
              const deal = deals.find(d => d.id === dealId);
              if (deal) goToDeal(deal);
            }}
          />
        )}
        {/* Full-screen presentation overlay */}
        {/* presentMode holds how to open the deck and, for a stored week,
            what to render. "export" lays every slide out as a printable
            document; a snapshot makes it rebuild that week instead of
            loading today's data. */}
        {presentMode && (
          <WeeklyPresentation
            exportMode={presentMode.mode === "export"}
            snapshot={presentMode.snapshot || null}
            presentedBy={account?.username || null}
            presentedByName={account?.name || null}
            onSnapshotSaved={() => setPresentationSaveTick(t => t + 1)}
            onClose={() => setPresentMode(null)}
          />
        )}
        </main>
      </div>

      {/* Proxy Analyst floating launcher — hidden for guests since they
          can't see the underlying data anyway. */}
    </div>
  );
}